// FCP design system — Icon primitive (v2.8.3)
//
// Replaces emoji-as-icon usage across the app with a single inline SVG set.
// Icons inherit currentColor and are rendered at the parent's font-size.
//
// Usage:
//   <Icon name="dashboard" />        // 1em × 1em
//   <Icon name="trash" size={18} />  // explicit size
//   <Icon name="check" size={14} className="my-class" />
//
// All paths are stroke-based with stroke-width 1.75 — this matches the
// editorial weight of Fraunces + Inter at typical UI sizes (16–24px).
// Filled glyphs (the few that exist) use currentColor as fill.
//
// Adding a new icon: add a key to ICON_PATHS. Keep viewBox 24×24 for the
// stroke icons and the path d="..." string. If you need a filled glyph
// (e.g. a status dot), use the { d, fill: true } shape.

import React from 'react';
import './Icon.css';

const STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

const FILLED = {
  fill: 'currentColor',
  stroke: 'none',
};

// All icons live on a 24×24 viewBox. Stroke icons by default; pass `filled`
// in the entry to render with currentColor fill instead.
const ICON_PATHS = {
  // Navigation
  dashboard:    'M4 13h7V4H4v9zm0 7h7v-5H4v5zm9 0h7V11h-7v9zm0-16v5h7V4h-7z',
  clock:        'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 7v5l3 2',
  calendar:     'M4 7a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7zM4 10h16M8 4v4M16 4v4',
  jobs:         'M5 4h14a1 1 0 0 1 1 1v15a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1zM8 9h8M8 13h8M8 17h5',
  media:        'M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1zM3 17l5-5 4 4 3-3 6 6M9 10a2 2 0 1 1-4 0 2 2 0 0 1 4 0z',
  payroll:      'M3 8h18a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1zM3 8V6a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v2M12 16a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
  workers:      'M9 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM3 21v-1a6 6 0 0 1 12 0v1M17 11a3 3 0 1 0 0-6M21 21v-1a5 5 0 0 0-4-4.9',
  billing:      'M3 7h18a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1zM2 11h20M6 16h2',
  estimates:    'M5 4h11l4 4v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1zM15 4v4h5M8 13h8M8 17h6',
  clients:      'M4 21V5a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v16M14 21V11a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v10M3 21h18M8 8h2M8 12h2M17 13h.01M17 17h.01',
  ai:           'M12 4v3M12 17v3M4.93 4.93l2.12 2.12M16.95 16.95l2.12 2.12M4.93 19.07l2.12-2.12M16.95 7.05l2.12-2.12M4 12h3M17 12h3M9 12a3 3 0 1 1 6 0 3 3 0 0 1-6 0z',
  reports:      'M3 21V5M3 21h18M7 17V11M11 17V8M15 17V13M19 17V6',
  activity:     'M3 12h4l3-8 4 16 3-8h4',
  notifications:'M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9zM10 21h4',
  profile:      'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21v-1a8 8 0 0 1 16 0v1',
  admin:        'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z',
  settings:     'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM4 12a8 8 0 0 1 1.4-4.5L4 6l2-2 1.5 1.4A8 8 0 0 1 12 4l.5-2h3l.5 2a8 8 0 0 1 4.5 1.4L22 4l2 2-1.4 1.5A8 8 0 0 1 24 12l2 .5v3l-2 .5a8 8 0 0 1-1.4 4.5L24 22l-2 2-1.5-1.4A8 8 0 0 1 16 24l-.5 2h-3l-.5-2a8 8 0 0 1-4.5-1.4L6 24l-2-2 1.4-1.5A8 8 0 0 1 4 16l-2-.5v-3z',
  // Sidebar simpler settings (gear)
  gear:         'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19 12a7 7 0 0 0-.1-1.2l2-1.6-2-3.4-2.4.8a7 7 0 0 0-2-1.2L14 4h-4l-.5 2.4a7 7 0 0 0-2 1.2l-2.4-.8-2 3.4 2 1.6a7 7 0 0 0 0 2.4l-2 1.6 2 3.4 2.4-.8a7 7 0 0 0 2 1.2L10 20h4l.5-2.4a7 7 0 0 0 2-1.2l2.4.8 2-3.4-2-1.6c.07-.4.1-.8.1-1.2z',
  // Actions
  close:        'M6 6l12 12M6 18L18 6',
  check:        'M5 12l5 5 9-11',
  plus:         'M12 5v14M5 12h14',
  edit:         'M16 4l4 4-12 12H4v-4L16 4zM14 6l4 4',
  trash:        'M4 7h16M9 7V4h6v3M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13M10 11v6M14 11v6',
  download:     'M12 4v12M6 12l6 6 6-6M4 20h16',
  upload:       'M12 20V8M6 12l6-6 6 6M4 4h16',
  search:       'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM21 21l-4.35-4.35',
  refresh:      'M3 12a9 9 0 0 1 15.5-6.4L21 8M21 3v5h-5M21 12a9 9 0 0 1-15.5 6.4L3 16M3 21v-5h5',
  link:         'M9 15l6-6M10 6l1.5-1.5a4 4 0 0 1 5.7 5.7L15 12M14 18l-1.5 1.5a4 4 0 0 1-5.7-5.7L9 12',
  copy:         'M8 8h11a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1zM4 16V4a1 1 0 0 1 1-1h12',
  eye:          'M12 5c5.5 0 9 7 9 7s-3.5 7-9 7-9-7-9-7 3.5-7 9-7zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
  eyeOff:       'M3 3l18 18M9.9 5.5A11.5 11.5 0 0 1 12 5c5.5 0 9 7 9 7a18 18 0 0 1-3 3.7M6 7.5C3.7 9.4 2 12 2 12s3.5 7 9 7c1.7 0 3.2-.5 4.5-1.3M9.5 9.5a3 3 0 0 0 4 4',
  arrowLeft:    'M19 12H5M12 19l-7-7 7-7',
  arrowRight:   'M5 12h14M12 5l7 7-7 7',
  chevronLeft:  'M15 18l-6-6 6-6',
  chevronRight: 'M9 18l6-6-6-6',
  chevronDown:  'M6 9l6 6 6-6',
  chevronUp:    'M6 15l6-6 6 6',
  // Four corners pushing out / pulling in — the conventional maximise and
  // restore. Added 2026-09-18 for the detail panel's full-screen toggle,
  // which was an arrowLeft/arrowRight pair beside the word "Full screen":
  // arrows mean NAVIGATE everywhere else in this app, so they were the
  // wrong glyph the moment the label went away.
  expand:       'M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7',
  collapse:     'M4 14h6v6M20 10h-6V4M14 10l7-7M3 21l7-7',
  hamburger:    'M4 6h16M4 12h16M4 18h16',
  bell:         'M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9zM10 21h4',
  mail:         'M3 6h18a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1zM3 7l9 6 9-6',
  logout:       'M16 17l5-5-5-5M21 12H9M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4',
  // Status / informational
  alert:        'M12 9v4M12 17v.01M10.3 4l-8 14a1 1 0 0 0 .9 1.5h17.6a1 1 0 0 0 .9-1.5l-8-14a1 1 0 0 0-1.7 0z',
  info:         'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 11v6M12 8v.01',
  pause:        'M9 5h2v14H9zM13 5h2v14h-2z',
  play:         'M7 4l13 8-13 8z',
  lock:         'M6 11h12a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1zM8 11V7a4 4 0 0 1 8 0v4',
  unlock:       'M6 11h12a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1zM8 11V7a4 4 0 0 1 7-2.5',
  pin:          'M12 22s-7-8-7-13a7 7 0 1 1 14 0c0 5-7 13-7 13zM12 11a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
  camera:       'M3 8h3l2-3h8l2 3h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1zM12 17a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
  document:     'M5 4h11l4 4v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1zM15 4v4h5M8 13h8M8 17h6',
  building:     'M4 21V5a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v16M14 21V11a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v10M3 21h18M8 8h2M8 12h2M17 13h.01M17 17h.01',
  // v2.8.34 — Kebab (vertical 3-dot) icon for MoreMenu trigger.
  // Three zero-length segments with stroke-linecap round render as
  // clean dots. Same pattern as the dots in the `clients` icon above.
  more:         'M12 5h.01M12 12h.01M12 19h.01',
  // 2026-09-01 — rebuild design system (client/src/ui/fw). Names the Field,
  // Dispatch and Office apps ask for; every one is a 24x24 stroke path.
  wifiOff:      'M2 2l20 20M8.5 16.5a5 5 0 0 1 7 0M5 13a10 10 0 0 1 5.2-2.7M12 6a15 15 0 0 1 10 4M2 10a15 15 0 0 1 3.5-2.5M16.5 13a10 10 0 0 0-2.1-1.4M12 20h.01',
  mapPin:       'M12 22s-7-8-7-13a7 7 0 1 1 14 0c0 5-7 13-7 13zM12 11a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
  alertTriangle:'M12 9v4M12 17v.01M10.3 4l-8 14a1 1 0 0 0 .9 1.5h17.6a1 1 0 0 0 .9-1.5l-8-14a1 1 0 0 0-1.7 0z',
  list:         'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01',
  users:        'M9 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM3 21v-1a6 6 0 0 1 12 0v1M17 11a3 3 0 1 0 0-6M21 21v-1a5 5 0 0 0-4-4.9',
  stop:         'M6 6h12v12H6z',
  x:            'M6 6l12 12M6 18L18 6',
  dollar:       'M12 2v20M17 6.5H9.5a3 3 0 0 0 0 6h5a3 3 0 0 1 0 6H6',
  fileText:     'M5 4h11l4 4v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1zM15 4v4h5M8 13h8M8 17h6M8 9h2',
  briefcase:    'M3 8h18a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1zM8 8V6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M2 13h20',
  home:         'M3 11l9-8 9 8M5 10v10a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1V10',
  phone:        'M5 3h4l2 5-2.5 1.5a11 11 0 0 0 6 6L16 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 5a2 2 0 0 1 2-2z',
  image:        'M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1zM3 17l5-5 4 4 3-3 6 6M9 10a2 2 0 1 1-4 0 2 2 0 0 1 4 0z',
  sun:          'M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10zM12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4',
  moon:         'M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z',
  // Filled status dots
  dot:          { d: 'M12 18a6 6 0 1 0 0-12 6 6 0 0 0 0 12z', filled: true },
};

export function Icon({ name, size, className = '', strokeWidth, ...rest }) {
  const entry = ICON_PATHS[name];
  if (!entry) {
    if (process.env.NODE_ENV !== 'production') {
      // Soft fail in dev so a typo doesn't blow up the whole tree.
      // eslint-disable-next-line no-console
      console.warn(`[Icon] unknown icon name: "${name}"`);
    }
    return null;
  }
  const isFilled = typeof entry === 'object' && entry.filled;
  const d = typeof entry === 'string' ? entry : entry.d;
  const dimension = size ? `${size}px` : '1em';

  return (
    <svg
      viewBox="0 0 24 24"
      width={dimension}
      height={dimension}
      aria-hidden="true"
      focusable="false"
      className={`fcp-icon ${className}`}
      {...rest}
    >
      <path
        d={d}
        {...(isFilled ? FILLED : { ...STROKE, ...(strokeWidth ? { strokeWidth } : {}) })}
      />
    </svg>
  );
}

export default Icon;
